import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ChevronUpIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../../context/AuthContext';
import { progressAPI } from '../../../services/api';
import type { WorkoutLog } from '../../../types';

function computeVolume(log: WorkoutLog): number {
  return log.exercises.reduce((total, ex) =>
    total + ex.sets.filter(s => s.completed).reduce((st, s) => st + s.weight * s.reps, 0), 0
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

export default function WorkoutHistory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<WorkoutLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (user?._id) loadLogs();
  }, [user]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await progressAPI.getWorkoutLogs(user!._id) as any;
      const sorted = (res?.logs || []).sort(
        (a: WorkoutLog, b: WorkoutLog) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      setLogs(sorted);
    } catch (err) {
      console.error('Load workout logs error:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleDelete = async (log: WorkoutLog) => {
    const id = log._id || log.id;
    if (!id || !user?._id) return;
    if (!confirm('Delete this workout log?')) return;
    setDeleting(id);
    try {
      await progressAPI.deleteWorkoutLog(user._id, id);
      setLogs(prev => prev.filter(l => (l._id || l.id) !== id));
    } catch (err) {
      console.error('Delete workout log error:', err);
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="text-5xl mb-4">🏋️</div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">No workouts logged yet</h3>
        <p className="text-gray-500 mb-6">Complete a workout to see your history here.</p>
        <button
          onClick={() => navigate('/workout-plans?tab=log')}
          className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors"
        >
          Start Your First Workout
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {logs.map((log) => {
        const id = log._id || log.id || log.date;
        const isOpen = expanded.has(id!);
        const volume = computeVolume(log);
        const completedSets = log.exercises.reduce((t, ex) => t + ex.sets.filter(s => s.completed).length, 0);

        return (
          <div key={id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => toggleExpand(id!)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-gray-900 truncate">
                    {log.name || 'Workout'}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{formatDate(log.date)}</p>
              </div>

              <div className="flex items-center gap-4 ml-4 flex-shrink-0">
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-medium text-gray-700">{log.exercises.length} exercises</p>
                  <p className="text-xs text-gray-500">{completedSets} sets · {volume > 0 ? `${volume.toLocaleString()} kg vol` : 'no weight logged'}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(log); }}
                  disabled={deleting === id}
                  className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
                {isOpen ? <ChevronUpIcon className="w-4 h-4 text-gray-400" /> : <ChevronDownIcon className="w-4 h-4 text-gray-400" />}
              </div>
            </div>

            {isOpen && (
              <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-3">
                {log.notes && (
                  <p className="text-sm text-gray-600 italic">{log.notes}</p>
                )}
                {log.exercises.map((ex, i) => (
                  <div key={ex.id || i}>
                    <p className="text-sm font-medium text-gray-800 mb-1">{ex.name}</p>
                    <div className="space-y-1">
                      {ex.sets.map((set, si) => (
                        <div key={si} className={`flex items-center gap-3 text-sm px-3 py-1.5 rounded-lg ${set.completed ? 'bg-green-50 text-green-800' : 'bg-gray-50 text-gray-500'}`}>
                          <span className="w-12 font-medium">Set {si + 1}</span>
                          <span>{set.weight > 0 ? `${set.weight} kg` : '—'}</span>
                          <span>×</span>
                          <span>{set.reps} reps</span>
                          {set.completed && <span className="ml-auto text-green-600 text-xs font-medium">✓</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
